import { PureComponent, createElement } from 'react';
import { createHocRef, Provider } from '@jupiter-app/utils';
import hoistNonReactStatic from 'hoist-non-react-statics';
import {
  AppContext,
  AppConfig,
  Plugin,
  InferableComponentEnhancerWithProps,
  AppCompProps,
  AppCompStatic,
  AppPassToProps,
} from './typings';
import pluginHelper, { pluginSpecialHookHelper } from './pluginHelper';

function hasPluginState(plugins: Plugin[]) {
  return plugins.some(
    plugin => plugin.__name__ === '@jupiter-app/plugin-state',
  );
}
function excludePluginRouterAndImmerIfHasReduck(
  hasReduck: boolean,
  plugins: Plugin[],
) {
  if (hasReduck) {
    return plugins.filter(
      plugin =>
        plugin.__name__ !== '@reduck/plugin-router' &&
        plugin.__name__ !== '@reduck/plugin-immer',
    );
  } else {
    return plugins;
  }
}

export default function createApp<OwnProps = {}>(
  config: AppConfig = {},
): InferableComponentEnhancerWithProps<
  AppPassToProps,
  OwnProps,
  AppCompStatic
> {
  const { plugins = [], extra = {} } = config;
  return createHocRef(
    WrappedComponent => {
      class WithApp extends PureComponent<AppCompProps> {
        appContext: AppContext = null as any;

        constructor(props: AppCompProps) {
          super(props);
          // node ssr, first render pass [_ssrData] (_ssrData.context)
          // node ssr, second render pass [_ssrData] (_ssrData.data | _ssrData.context)
          // browser render pass [_ssrData]
          const { _ssrData, _loadersPromiseMap, store } = props;
          const isHasState = hasPluginState(plugins);
          this.appContext = {
            // the following two fields from reduck store
            extra:
              store && store._context && store._context.extra
                ? Object.assign(store._context.extra, extra)
                : extra,
            Plugins:
              store && store._context && store._context.Plugins
                ? store._context.Plugins
                : {},

            isBrowser: Boolean(process.browser),

            _pluginHelper: null as any,
            _pluginsMap: {},
            _ssrData: {},
            _loaderId: 0,
            _loadersPromiseMap:
              // node ssr second render || node ssr first render || browser
              (_ssrData && _ssrData.data && _ssrData.data.loadersData) ||
              _loadersPromiseMap ||
              {},
            // 服务端第二次渲染 或 客户端渲染会有该值
            _storeState: _ssrData?.data?.storeState,
            // used to distinguish whether using @jupiter-app/plugin-state(reduck) plugin
            __RUNTIME__: isHasState ? 'pro' : 'lite',
          };
          // ssr-plugin
          _ssrData &&
            _ssrData.context &&
            ((this.appContext as any)._context = _ssrData && _ssrData.context);
          this.appContext._context = {
            ...this.appContext._context,
            utils: {},
          };
          // state-plugin
          store && ((this.appContext as any).store = store);

          this.appContext._pluginHelper = pluginHelper(
            {
              plugins: excludePluginRouterAndImmerIfHasReduck(
                isHasState,
                plugins,
              ),
            },
            this.appContext,
          );
        }

        render() {
          const { store, _loadersPromiseMap, ...extraProps } = this.props;
          const WrappedElement = createElement(
            WrappedComponent,
            extraProps,
            this.props.children,
          );
          // 遍历 plugin.Provider
          const root = this.appContext._pluginHelper.composeProvider(
            WrappedElement,
            { ...config, ...this.props },
          );

          return createElement(Provider, { value: this.appContext }, root);
        }
      }

      // NOTICE: if ssr, it's easy to get original WrappedComponent and configs
      (WithApp as any).__App__ = WrappedComponent;
      (WithApp as any).__config__ = config;

      return pluginSpecialHookHelper({ plugins }).composeOnCreateApp(
        hoistNonReactStatic(WithApp, WrappedComponent),
        config,
      );
    },
    {
      // not to be hoisted
      blacklist: ['init', 'prefetch'],
    },
  ) as any;
}
